import { TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';

/**
 * Metadata da tabela de Acompanhamento.
 *
 * <p>Fora do componente de propósito: a declaração de colunas é configuração, não comportamento, e
 * misturada ao componente ela se perdia entre os métodos de ação, o cache de fotos e o mapeamento
 * de linha. Aqui ela se lê de uma vez, e a mesma forma serve de molde para as outras tabelas.
 *
 * <p>É uma função, e não uma constante: o `hidden` é reescrito pelo seletor de colunas, então cada
 * instância precisa do próprio objeto. Uma constante compartilhada faria duas tabelas na mesma tela
 * disputarem o mesmo estado de visibilidade.
 *
 * <h4>Como as colunas se dividem</h4>
 * <ul>
 *   <li><b>Declarativas</b> — identificação, criação e data e horário: valor principal mais uma
 *       linha secundária (`sub`), com a tipografia de dado gerado por máquina (`enfase`).</li>
 *   <li><b>Perfil</b> — assistente: retrato e nome, pelo tipo `perfil` do próprio CustomTable.</li>
 *   <li><b>Sob medida</b> (`celulaCustomizada`) — só informações, que são dois grupos de sentidos
 *       diferentes (estados e marcas) separados por filete, em duas linhas. O filete e o
 *       agrupamento não têm equivalente declarativo, e são justamente o que torna a célula
 *       legível.</li>
 * </ul>
 */
export function construirMetadataReunioes(): TableMetadata {
    return {
        headers: {
            codigoExibicao: {
                type: 'text',
                value: 'Identificação',
                hidden: false,
                default: '-',
                enfase: { forte: true, maquina: true },
                sub: { key: 'protocoloChip', type: 'tag', value: 'Protocolo', default: '-', enfase: { maquina: true } }
            },
            diaCadastro: {
                type: 'text',
                value: 'Criada em',
                hidden: false,
                default: '-',
                enfase: { forte: true, maquina: true },
                sub: { key: 'horaCadastro', type: 'text', enfase: { maquina: true } }
            },
            // Título da comissão em cima; embaixo, selo da modalidade e local lado a lado — dois
            // subs na mesma linha, que é como se leem ("[Híbrida] Auditório"). Sem modalidade
            // informada, o local fica sozinho, sem buraco.
            tituloComissao: {
                type: 'text',
                value: 'Comissão / CT / GT e Local',
                hidden: false,
                default: '-',
                enfase: { forte: true },
                // "Não Informada" é o texto que o backend manda quando não há modalidade — vale como
                // ausência, e um selo emoldurando essa frase leria como se fosse um dado.
                sub: [
                    { key: 'modalidade', type: 'tag', value: 'Modalidade', default: 'Não Informada' },
                    { key: 'localTexto', type: 'text', value: 'Local', default: '-' }
                ]
            },
            // Aponta para `assistentePerfil`, e não para `assistente`: o segundo segue sendo o nome
            // cru, que o cartão e o expandido usam. Um campo por forma de leitura.
            assistentePerfil: { type: 'perfil', value: 'Assistente', hidden: false, default: 'Não Informado' },
            diaInicio: {
                type: 'text',
                value: 'Data e Horário',
                hidden: false,
                default: '-',
                enfase: { forte: true, maquina: true },
                sub: { key: 'faixaHorarioTexto', type: 'text', enfase: { maquina: true } }
            },
            status: { type: 'tag', value: 'Informações', hidden: false, default: '-', celulaCustomizada: true }
        },
        title: 'nomeComissao',
        tag: 'status'
    };
}
